import { create } from 'zustand';
import { safeStorage } from '@/lib/utils';
import { decodeJwt, type JwtClaims } from './jwt';
import { isPlatformRole } from './roles';

export const REFRESH_TOKEN_KEY = 'jdap.refreshToken';
export const SELECTED_TENANT_KEY = 'jdap.selectedTenantId';

export interface SessionUser {
  id: string;
  email: string;
  roles: string[];
  caps: string[];
  tenantId: string | null;
}

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
  /** ISO timestamp when the access token expires (platform TokenResponse). */
  expiresAt?: string;
  /** Seconds until the access token expires (legacy/alternative shape). */
  expiresIn?: number;
  user?: Partial<SessionUser>;
}

interface AuthState {
  /** Access token lives in memory only. */
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  /** Tenant scope chosen by a platform role (sent as X-Tenant-Id). Tenant users are pinned to their JWT tenant. */
  selectedTenantId: string | null;
  setSession: (tokens: SessionTokens) => void;
  clear: () => void;
  selectTenant: (tenantId: string | null) => void;
}

function userFrom(claims: JwtClaims | null, fallback?: Partial<SessionUser>): SessionUser | null {
  if (!claims && !fallback) return null;
  return {
    id: fallback?.id ?? claims?.sub ?? '',
    email: fallback?.email ?? claims?.email ?? '',
    roles: fallback?.roles ?? claims?.roles ?? [],
    caps: fallback?.caps ?? claims?.caps ?? [],
    tenantId: fallback?.tenantId ?? claims?.tenantId ?? null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  refreshToken: safeStorage.get(REFRESH_TOKEN_KEY),
  user: null,
  selectedTenantId: safeStorage.get(SELECTED_TENANT_KEY),
  setSession: (tokens) => {
    safeStorage.set(REFRESH_TOKEN_KEY, tokens.refreshToken);
    // JWT claims are authoritative for roles/caps/tenant; the response body fills gaps (e.g. email).
    const claims = decodeJwt(tokens.accessToken);
    const user = userFrom(claims, claims ? { email: tokens.user?.email, id: tokens.user?.id } : tokens.user);
    const cleaned = user ? { ...user, email: user.email || tokens.user?.email || '' } : null;
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken, user: cleaned });
  },
  clear: () => {
    safeStorage.remove(REFRESH_TOKEN_KEY);
    set({ accessToken: null, refreshToken: null, user: null });
  },
  selectTenant: (tenantId) => {
    if (tenantId) safeStorage.set(SELECTED_TENANT_KEY, tenantId);
    else safeStorage.remove(SELECTED_TENANT_KEY);
    set({ selectedTenantId: tenantId });
  },
}));

/** The tenant scope for API requests: JWT tenant for tenant users, the selected tenant for platform roles. */
export function effectiveTenantId(state: Pick<AuthState, 'user' | 'selectedTenantId'>): string | null {
  if (!state.user) return null;
  if (state.user.tenantId) return state.user.tenantId;
  return isPlatformRole(state.user.roles) ? state.selectedTenantId : null;
}
